import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, XCircle, ShieldCheck, Loader2, FileText, Search, Download, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All records" },
];

function SecureFile({ path }) {
  const [url, setUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let objUrl;
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/verification/file/${path}`, { responseType: "blob" });
        if (!active) return;
        objUrl = URL.createObjectURL(res.data);
        setIsPdf(res.data.type === "application/pdf");
        setUrl(objUrl);
      } catch (e) { if (active) setErr(true); }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);

  if (err) return <div className="w-full h-40 rounded-lg bg-[#1A1A1A] flex items-center justify-center text-xs text-[#E63946]">Failed to load</div>;
  if (!url) return <div className="w-full h-40 rounded-lg bg-[#1A1A1A] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={20} /></div>;
  if (isPdf) return (
    <a href={url} target="_blank" rel="noreferrer" className="w-full h-40 rounded-lg bg-[#1A1A1A] flex flex-col items-center justify-center gap-2 text-[#D4AF37] text-xs hover:bg-[#222]">
      <FileText size={28} /> Open PDF
    </a>
  );
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="w-full h-40 object-cover rounded-lg border border-[#2A2A2A]" /></a>;
}

export default function AdminVerifications() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/verifications?status=${status}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/verifications/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `2257-records-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Records exported");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [it.legal_name, it.user?.name, it.user?.email].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  const review = async (id, approve) => {
    let reason = null;
    if (!approve) {
      reason = window.prompt("Reason for rejection (shown to the user):", "ID unclear or details don't match");
      if (reason === null) return;
    }
    setBusy(id);
    try {
      await api.post("/admin/verifications/review", { request_id: id, approve, reason });
      toast.success(approve ? "User verified ✅" : "Verification rejected");
      await load(tab);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (user && user.role !== "admin") {
    return <div className="p-12 text-center text-[#A0A0A0]">Admins only.</div>;
  }

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <div className="overline mb-1">Admin · Trust & Safety</div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-heading text-3xl md:text-4xl flex items-center gap-3">
          <ShieldCheck className="text-[#D4AF37]" /> ID Records Vault
        </h2>
        <button data-testid="verif-export" onClick={exportCsv} disabled={exporting}
          className="btn-outline-gold px-5 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-60">
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export register (CSV)
        </button>
      </div>

      <div data-testid="retention-note" className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] p-4 mb-6 flex items-start gap-3">
        <Archive size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[#A0A0A0] leading-relaxed">
          <span className="text-white font-medium">18 U.S.C. §2257 record-keeping.</span> Age & identity records for every verified member are stored securely and retained for <span className="text-white">7 years</span>. ID documents are encrypted at rest and only accessible to admins. Export the register regularly and keep an offline copy for your Custodian of Records.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`verif-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-full text-sm transition-colors ${tab === t.key ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
        <input data-testid="verif-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by legal name, display name or email…"
          className="pe-input w-full rounded-full pl-11 pr-4 py-2.5 text-sm" />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="verif-empty" className="py-20 text-center text-[#A0A0A0]">No {tab === "all" ? "" : tab} records {q ? "match your search" : "yet"}.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((it) => (
            <motion.div key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`verif-card-${it.id}`} className="pe-card rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full overflow-hidden border border-[#2A2A2A] bg-[#1A1A1A] flex-shrink-0">
                  {it.user?.photo && <img src={it.user.photo} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{it.user?.name || "Unknown"}</div>
                  <div className="text-xs text-[#A0A0A0] truncate">{it.user?.email}</div>
                </div>
                {it.user?.is_creator && <span className="ml-auto text-[10px] px-2 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">Creator</span>}
              </div>

              <div className="text-xs text-[#A0A0A0] space-y-1 mb-4">
                <div><span className="text-white">Legal name:</span> {it.legal_name}</div>
                <div><span className="text-white">Date of birth:</span> {it.date_of_birth}</div>
                <div><span className="text-white">Submitted:</span> {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}</div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="text-[11px] text-[#A0A0A0] mb-1">ID document</div>
                  <SecureFile path={it.id_doc_path} />
                </div>
                <div>
                  <div className="text-[11px] text-[#A0A0A0] mb-1">Selfie</div>
                  <SecureFile path={it.selfie_path} />
                </div>
              </div>

              {it.status === "pending" ? (
                <div className="flex gap-3">
                  <button data-testid={`verif-approve-${it.id}`} onClick={() => review(it.id, true)} disabled={busy === it.id}
                    className="btn-gold flex-1 rounded-full py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                    <BadgeCheck size={16} /> Approve
                  </button>
                  <button data-testid={`verif-reject-${it.id}`} onClick={() => review(it.id, false)} disabled={busy === it.id}
                    className="flex-1 rounded-full py-2 text-sm flex items-center justify-center gap-2 border border-[#E63946]/50 text-[#E63946] hover:bg-[#E63946]/10 disabled:opacity-60">
                    <XCircle size={16} /> Reject
                  </button>
                </div>
              ) : (
                <div>
                  <div className={`text-sm flex items-center gap-2 ${it.status === "approved" ? "text-[#22c55e]" : "text-[#E63946]"}`}>
                    {it.status === "approved" ? <><BadgeCheck size={16} /> Approved</> : <><XCircle size={16} /> Rejected — {it.reject_reason}</>}
                  </div>
                  {(it.reviewed_at || it.reviewed_by_name) && (
                    <div className="text-[11px] text-[#A0A0A0] mt-1.5" data-testid={`verif-audit-${it.id}`}>
                      {it.reviewed_by_name ? <>by <span className="text-white">{it.reviewed_by_name}</span></> : null}
                      {it.reviewed_at ? ` · ${new Date(it.reviewed_at).toLocaleString()}` : ""}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
